export interface SplitResult {
  firstPart: string;
  secondPart: string;
}

/**
 * Intelligently splits a string for truncation purposes, following grammatical rules
 * and handling various naming conventions (camelCase, snake_case, kebab-case).
 *
 * @param input - The string to split
 * @param maxFirstPart - Maximum characters allowed in the first part (default: 35)
 * @returns An object with firstPart and secondPart strings
 */
export function smartSplitString(
  input: string,
  maxFirstPart: number = 35,
): SplitResult {
  if (input.length <= maxFirstPart) {
    return { firstPart: input.trim(), secondPart: '' };
  }

  // Find potential split points within the first maxFirstPart characters
  const potentialSplits: number[] = [];

  for (let i = 1; i < Math.min(input.length, maxFirstPart + 1); i++) {
    const char = input[i];
    const prevChar = input[i - 1];

    // Space boundaries (highest priority)
    if (char === ' ') {
      potentialSplits.push(i);
    }
    // Underscore and hyphen boundaries
    else if (char === '_' || char === '-') {
      potentialSplits.push(i);
    }
    // camelCase boundaries (lowercase followed by uppercase)
    else if (prevChar && /[a-z]/.test(prevChar) && /[A-Z]/.test(char)) {
      potentialSplits.push(i);
    }
    // Number to letter boundaries
    else if (prevChar && /\d/.test(prevChar) && /[a-zA-Z]/.test(char)) {
      potentialSplits.push(i);
    }
    // Letter to number boundaries
    else if (prevChar && /[a-zA-Z]/.test(prevChar) && /\d/.test(char)) {
      potentialSplits.push(i);
    }
  }

  // Find the best split point (latest valid point within maxFirstPart)
  const validSplits = potentialSplits.filter((pos) => pos <= maxFirstPart);

  if (validSplits.length > 0) {
    const splitPoint = Math.max(...validSplits);
    return {
      firstPart: input.slice(0, splitPoint).trim(),
      secondPart: input.slice(splitPoint).trim(),
    };
  }

  // fallback: simple truncation at maxFirstPart
  return {
    firstPart: input.slice(0, maxFirstPart).trim(),
    secondPart: input.slice(maxFirstPart).trim(),
  };
}
